import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CUSTOM_FIELD_ENTITY_DEFS,
  CUSTOM_FIELD_TYPES,
  CUSTOM_FIELD_TYPE_MAP,
  MAX_CUSTOM_FIELDS,
  type CustomFieldDef,
  type CustomFieldEntity,
  type CustomFieldType,
} from '@goyone/shared';
import { api } from '../../lib/api';
import {
  Badge,
  EmptyState,
  ErrorState,
  Modal,
  PageHead,
  TableSkeleton,
} from '../../components/ui';

/**
 * إدارة الحقول المخصَّصة.
 *
 * الحقل هنا يظهر في نموذج يملؤه الفريق كله، ويصير عمودًا في كل تصدير
 * وبُعدًا في باني التقارير. فالشاشة تُظهر هذه الآثار الثلاثة صراحةً: من
 * يضيف حقلًا يجب أن يعرف أنه لا يضيف خانة في شاشة واحدة.
 */
export function CustomFieldsPage() {
  /*
   * «ملف العميل» تخصيصٌ واحد: العميل والعميل المحتمل نموذجهما واحد،
   * فالحقل يُعرَّف مرةً ويظهر في الملفّين — والمخزن القانوني LEAD.
   */
  const [entity] = useState<CustomFieldEntity>('LEAD');
  const [editing, setEditing] = useState<CustomFieldDef | null>(null);
  const [creating, setCreating] = useState(false);

  const queryClient = useQueryClient();

  const fields = useQuery({
    queryKey: ['custom-fields', entity],
    queryFn: () => api<CustomFieldDef[]>(`/custom-fields?entity=${entity}`),
  });

  const toggle = useMutation({
    mutationFn: (f: CustomFieldDef) =>
      api(`/custom-fields/${f.id}`, { method: 'PATCH', body: { isActive: !f.isActive } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['custom-fields'] }),
  });

  const remove = useMutation({
    mutationFn: (f: CustomFieldDef) => api(`/custom-fields/${f.id}`, { method: 'DELETE' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['custom-fields'] }),
  });

  const rows = fields.data ?? [];
  const activeCount = rows.filter((f) => f.isActive).length;

  return (
    <div className="page">
      <PageHead
        title="الحقول المخصَّصة"
        subtitle="خانات إضافية تضيفها إلى نماذج النظام"
        actions={
          <button
            className="btn btn-primary"
            onClick={() => setCreating(true)}
            disabled={rows.length >= MAX_CUSTOM_FIELDS}
          >
            حقل جديد
          </button>
        }
      />

      {fields.isError && <ErrorState error={fields.error} onRetry={() => fields.refetch()} />}

      {/* نموذجٌ واحد للملفّين — فلا تبويبين يفترقان بحقولهما. */}
      <div className="tabs" role="tablist">
        <button role="tab" aria-selected className="tab">
          ملف العميل
        </button>
      </div>

      <div className="alert alert-info mb-4">
        كل حقل تضيفه هنا يظهر في نموذج ملف العميل — للعميل المحتمل وللعميل (فردًا أو شركة)
        سواء — ويصير عمودًا باسمه في ملف التصدير وفي قالب الاستيراد، ويصلح للتقسيم في باني
        التقارير. وما جُمع قبل التحويل يبقى مقروءًا بعده لأن التعريف واحد.
      </div>

      <div className="card">
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>العنوان</th>
                <th>النوع</th>
                <th>مطلوب</th>
                <th>الخيارات</th>
                <th>الحالة</th>
                <th />
              </tr>
            </thead>
            {fields.isLoading && <TableSkeleton rows={4} cols={6} />}

            <tbody>
              {rows.map((f) => (
                <tr key={f.id} style={f.isActive ? undefined : { opacity: 0.55 }}>
                  <td>
                    <span className="cell-strong">{f.label}</span>
                    {f.hint && <div className="tiny mute">{f.hint}</div>}
                  </td>
                  <td>
                    <Badge kind={CUSTOM_FIELD_TYPE_MAP[f.type].kind}>
                      {CUSTOM_FIELD_TYPE_MAP[f.type].label}
                    </Badge>
                  </td>
                  <td className="cell-mute">{f.required ? 'نعم' : '—'}</td>
                  <td className="cell-mute">
                    {f.options.length ? f.options.join('، ') : '—'}
                  </td>
                  <td>
                    <Badge kind={f.isActive ? 'success' : 'neutral'}>
                      {f.isActive ? 'فعّال' : 'معطَّل'}
                    </Badge>
                  </td>
                  <td>
                    <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                    <button className="btn btn-sm" onClick={() => setEditing(f)}>
                      تعديل
                    </button>
                    <button
                      className="btn btn-sm"
                      onClick={() => toggle.mutate(f)}
                      disabled={toggle.isPending}
                    >
                      {f.isActive ? 'تعطيل' : 'تفعيل'}
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => {
                        if (
                          window.confirm(
                            `حذف «${f.label}» يمحو قيمه المحفوظة في كل السجلات ولا يُسترجع. ` +
                              'التعطيل يُخفيه من النماذج ويُبقي البيانات. أتريد الحذف؟',
                          )
                        ) {
                          remove.mutate(f);
                        }
                      }}
                      disabled={remove.isPending}
                    >
                      حذف
                    </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!fields.isLoading && rows.length === 0 && (
          <EmptyState
            title="لا حقول مخصَّصة بعد"
            text="أضف خانة لبيانات تجمعها ولا يعرفها النظام: رقم سجل، اسم مسؤول، موعد تجديد."
          />
        )}

        {rows.length > 0 && (
          <div className="pager">
            <span>
              {activeCount} فعّال من {rows.length}، والحد {MAX_CUSTOM_FIELDS} حقلًا.
            </span>
          </div>
        )}
      </div>

      {(creating || editing) && (
        <FieldDialog
          entity={entity}
          field={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

const CUSTOM_FIELD_ENTITY_MAP_LABEL: Record<CustomFieldEntity, string> = {
  ACCOUNT: 'العميل',
  LEAD: 'العميل المحتمل',
};

function FieldDialog({
  entity,
  field,
  onClose,
}: {
  entity: CustomFieldEntity;
  field: CustomFieldDef | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const editing = Boolean(field);

  const [label, setLabel] = useState(field?.label ?? '');
  const [type, setType] = useState<CustomFieldType>(field?.type ?? 'TEXT');
  const [options, setOptions] = useState(field?.options.join('\n') ?? '');
  const [required, setRequired] = useState(field?.required ?? false);
  const [hint, setHint] = useState(field?.hint ?? '');

  const save = useMutation({
    mutationFn: () => {
      const body = {
        label: label.trim(),
        type,
        options:
          type === 'SELECT'
            ? options
                .split('\n')
                .map((o) => o.trim())
                .filter(Boolean)
            : [],
        required,
        hint: hint.trim() || null,
      };

      return editing
        ? api(`/custom-fields/${field!.id}`, { method: 'PATCH', body })
        : api('/custom-fields', { method: 'POST', body: { entity, ...body } });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['custom-fields'] });
      onClose();
    },
  });

  const optionCount = options.split('\n').filter((o) => o.trim()).length;
  const valid = label.trim().length >= 1 && (type !== 'SELECT' || optionCount >= 2);

  return (
    <Modal
      title={editing ? 'تعديل الحقل' : 'حقل جديد'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={!valid || save.isPending}
          >
            {save.isPending ? 'جارٍ الحفظ' : 'حفظ'}
          </button>
        </>
      }
    >
      {save.isError && (
        <div className="alert alert-danger mb-4">
          {save.error instanceof Error ? save.error.message : 'تعذّر الحفظ.'}
        </div>
      )}

      <div className="form-grid">
        <div className="field span-2">
          <label className="label req" htmlFor="cf-label">
            العنوان
          </label>
          <input
            id="cf-label"
            className="input"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="رقم السجل التجاري"
          />
          <span className="tiny mute">
            هذا العنوان يصير اسم العمود في ملفات التصدير والاستيراد.
          </span>
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="cf-type">
            النوع
          </label>
          <select
            id="cf-type"
            className="select"
            value={type}
            onChange={(e) => setType(e.target.value as CustomFieldType)}
          >
            {CUSTOM_FIELD_TYPES.map((t) => (
              <option key={t} value={t}>
                {CUSTOM_FIELD_TYPE_MAP[t].label}
              </option>
            ))}
          </select>
          <span className="tiny mute">{CUSTOM_FIELD_TYPE_MAP[type].description}</span>
        </div>

        {type === 'SELECT' && (
          <div className="field span-2">
            <label className="label req" htmlFor="cf-options">
              الخيارات
            </label>
            <textarea
              id="cf-options"
              className="input"
              rows={5}
              value={options}
              onChange={(e) => setOptions(e.target.value)}
              placeholder={'حكومي\nخاص\nأفراد'}
            />
            <span className="tiny mute">خيار في كل سطر، واثنان على الأقل.</span>
          </div>
        )}

        <div className="field span-2">
          <label className="label" htmlFor="cf-hint">
            نص إرشادي
          </label>
          <input
            id="cf-hint"
            className="input"
            value={hint}
            onChange={(e) => setHint(e.target.value)}
          />
          <span className="tiny mute">يظهر تحت الخانة في النموذج.</span>
        </div>
      </div>

      <label className="row mt-4" style={{ cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={required}
          onChange={(e) => setRequired(e.target.checked)}
        />
        <span className="small">
          حقل مطلوب — لا يُحفظ السجل بدونه، ولا يُستورَد صفٌّ لا يحمله
        </span>
      </label>

      {editing && (
        /*
          تغيير النوع بعد حفظ قيم يجعل ما حُفظ غير مقروء بالنوع الجديد،
          فيمنعه الخادم. ذكره هنا يوفّر على المستخدم محاولة تُرفض.
        */
        <div className="alert alert-info mt-4">
          العنوان يُعدَّل متى شئت والقيم المحفوظة تتبعه. أما النوع فلا يُغيَّر بعد حفظ قيم:
          أنشئ حقلًا جديدًا وعطّل هذا.
        </div>
      )}
    </Modal>
  );
}
